import { useEffect, useRef, useState } from 'react';
import { uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { StudioSheet } from '../studio/StudioSheet';
import { StudioIcon } from '../studio/StudioIcon';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import './native-photo-styles.css';
import { record } from '../studio/studio-content';
import { studioMediaUrl } from '../studio/studio-preview-model';
import { choosePhotoStyle, photoThemes, type PhotoPolicy } from './photo-model';
import { PhotoFieldInput } from './PhotoFieldInput';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { reduced } from '../../ui/motion';
import { SwitchRow } from '../../ui/SwitchRow';
import { StudioReferenceCard, StudioReferencePlate } from '../studio/StudioReference';
import { useStudioReference } from '../studio/use-studio-reference';
import { StudioUnitPrice } from '../studio/StudioMediaHeader';
import { shootKinds } from '../studio/studio-shoot-model';
export function PhotoStyles({
  state,
  policy,
  onSelectStyle,
  onReferenceOpen,
  selectedId,
  allowNone = false,
  initialReferenceOpen = false,
  unit = 'photo',
  unitPrice,
  unitTotal = false,
  themeOrder,
}: {
  state: CircleFormState;
  policy: PhotoPolicy;
  onSelectStyle?(id: string): void;
  onReferenceOpen?(): void;
  selectedId?: string;
  allowNone?: boolean;
  initialReferenceOpen?: boolean;
  /** Единица цены в карточке стиля: «30 C за фото» или «12 C за секунду» */
  unit?: 'photo' | 'video';
  /** Цена за единицу из ответа сервера (app.price) */
  unitPrice?: unknown;
  /** Цена — за запуск шаблона («60 C»), без единицы */
  unitTotal?: boolean;
  /** Порядок чипов тем листа «Все стили» из документа экрана */
  themeOrder?: readonly string[];
}) {
  const [all, setAll] = useState(false),
    [query, setQuery] = useState(''),
    [tab, setTab] = useState('all'),
    [theme, setTheme] = useState(''),
    [preview, setPreview] = useState<string | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null),
    heldNow = useRef(false);
  const rail = useRef<HTMLDivElement>(null);
  const [referenceOpen, setReferenceOpen] = useState(initialReferenceOpen);
  const referenceField = state.form?.fields.find(field => field.key === 'reference');
  const referencePreview = useStudioPhotoPreviews(state, referenceField ? [referenceField] : []);
  const [held, setHeld] = useState(false);
  const reference = useStudioReference({
    state,
    field: referenceField,
    fallbackStyle: policy.default_style_id,
    previewUrl: referencePreview.urls.reference,
  });
  useEffect(
    () => () => {
      if (hold.current) clearTimeout(hold.current);
    },
    [],
  );
  const referenceOn = !!state.inputs.reference,
    selected = selectedId ?? state.inputs.style;
  useEffect(() => {
    const element = rail.current;
    if (!element) return;
    const sync = () => {
      element.style.setProperty('--ml', element.scrollLeft > 4 ? '24px' : '0px');
      element.style.setProperty('--mr', element.scrollWidth - element.scrollLeft - element.clientWidth > 4 ? '24px' : '0px');
    };
    const picked = element.querySelector<HTMLElement>('.bcs-c.on');
    if (picked) {
      const bounds = element.getBoundingClientRect(),
        target = picked.getBoundingClientRect();
      if (target.right < bounds.left + 24 || target.left > bounds.right - 24)
        element.scrollTo?.({
          left: element.scrollLeft + target.left - bounds.left - 24,
          behavior: reduced() ? 'auto' : 'smooth',
        });
    }
    sync();
    element.addEventListener('scroll', sync, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    observer?.observe(element);
    return () => {
      element.removeEventListener('scroll', sync);
      observer?.disconnect();
    };
  }, [selected, policy.styles.length]);
  const styles = policy.styles
    .filter(
      style =>
        style.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()) &&
        (!theme || style.themes.includes(theme)) &&
        (tab === 'all' || (tab === 'new' && style.isNew) || (tab === 'pop' && (style.hit || style.popularity > 0))),
    )
    .sort((a, b) => (tab === 'pop' ? b.popularity - a.popularity : 0));
  const themes = photoThemes(policy, themeOrder);
  const card = policy.styles.find(style => style.id === preview);
  const choose = (id: string) => {
    if (onSelectStyle) {
      onSelectStyle(id);
      return;
    }
    state.setInputs(current => {
      const next = choosePhotoStyle(current, id);
      if (policy.styles.find(style => style.id === id)?.look === 'drawn' && record(state.form).shoot) {
        let shoot: Record<string, string> = {};
        try {
          shoot = JSON.parse(next.shoot ?? '{}');
        } catch {
          /* Повреждённый локальный выбор сбрасывается. */
        }
        /* Рисованный стиль гасит «Камеру», «Объектив» и «Диафрагму» — только строки, что есть на экране: у ролика и у «Изменить видео»
           сервер оставляет из них одну «Камеру» (MessagingVideoPolicy.ShootingScreen), лишний ключ — 422 not_on_screen */
        const edit = record(state.form?.constraints?.video).mode === 'edit';
        const kinds = shootKinds(state.form, edit ? { ...next, video: next.video || 'edit' } : next);
        for (const kind of ['camera', 'lens', 'aperture'])
          if (kinds.includes(kind)) shoot[kind] = 'auto';
          else delete shoot[kind];
        if (Object.keys(shoot).length) next.shoot = JSON.stringify(shoot);
        else delete next.shoot;
      }
      return next;
    });
  };
  const cancelHold = () => {
    if (hold.current) clearTimeout(hold.current);
    hold.current = null;
  };
  const fromGrid = useRef(false);
  const railStyles = [...policy.styles].sort((a, b) => b.popularity - a.popularity).slice(0, 8);
  const chosen = policy.styles.find(style => style.id === selected);
  /* выбранный в листе стиль, которого нет в ленте, встаёт сразу за «Без стиля» — выбор всегда виден (styleRail шаблона, legacy/bot-core.js) */
  if (chosen && !railStyles.some(style => style.id === chosen.id)) railStyles.unshift(chosen);
  const openPreview = (id: string, grid: boolean) => {
    fromGrid.current = grid;
    setAll(false);
    setPreview(id);
  };
  const closePreview = () => {
    setPreview(null);
    if (fromGrid.current) setAll(true);
  };
  const pick = (id: string) => {
    choose(id);
    setAll(false);
    setPreview(null);
  };
  const styleCard = (style: PhotoPolicy['styles'][number], grid = false) => (
    <div className="studio-style-item" key={style.id}>
      <button
        type="button"
        className={`bcs-c${!referenceOn && selected === style.id ? ' on' : ''}`}
        aria-pressed={!referenceOn && selected === style.id}
        disabled={state.locked}
        onClick={() => {
          if (heldNow.current) {
            heldNow.current = false;
            return;
          }
          pick(style.id);
        }}
        onPointerDown={() => {
          cancelHold();
          heldNow.current = false;
          hold.current = setTimeout(() => {
            heldNow.current = true;
            setHeld(true);
            openPreview(style.id, grid);
          }, 450);
        }}
        onPointerUp={cancelHold}
        onPointerCancel={cancelHold}
        onPointerLeave={cancelHold}
      >
        <span className="pc">
          {studioMediaUrl(style.photos[0]) ? (
            <img src={studioMediaUrl(style.photos[0])} alt="" loading="lazy" />
          ) : (
            <span className="bcs-no">
              <StudioIcon kind="photo" />
            </span>
          )}
          {(style.hit || style.isNew) && <span className="nb">{uiText(style.hit ? 'Хит' : 'Новое')}</span>}
          <i className="ck" aria-hidden="true">
            <StudioIcon kind="check" />
          </i>
        </span>
        <b>{style.name}</b>
      </button>
      <button
        type="button"
        className="studio-style-peek-button"
        aria-label={`${uiText('О стиле')} · ${style.name}`}
        onClick={() => openPreview(style.id, grid)}
      >
        ↗
      </button>
    </div>
  );
  const none = allowNone || state.form?.fields.find(field => field.key === 'style')?.values.includes('none');
  return (
    <section className="bcs photo-styles" aria-label={uiText('Стиль')}>
      <div className="glbl bcs-gl">
        <span>{uiText('Стиль')}</span>
        <button type="button" className="lnk" onClick={() => setAll(true)}>
          {uiText('Все')} <span className="mono">{policy.styles.length}</span>
          <StudioIcon kind="chev" />
        </button>
      </div>
      <div ref={rail} className="bcs-r studio-style-rail">
        {(referenceField || onReferenceOpen) &&
          policy.reference_available &&
          (onReferenceOpen || !referenceField ? (
            <button
              type="button"
              className={`bcs-c reference brf-c pd-own${referenceOn ? ' on' : ''}`}
              aria-pressed={referenceOn}
              disabled={state.locked}
              onClick={() => (onReferenceOpen ? onReferenceOpen() : setReferenceOpen(true))}
            >
              <span className="pc">
                <span className="pd-oi">
                  <i>
                    <StudioIcon kind="photoPlus" />
                  </i>
                  {uiText('Загрузить картинку')}
                </span>
                <i className="ck" aria-hidden="true">
                  <StudioIcon kind="check" />
                </i>
              </span>
              <b>{uiText('Свой референс')}</b>
            </button>
          ) : (
            <StudioReferenceCard reference={reference} disabled={state.locked} />
          ))}
        {none && (
          <button
            type="button"
            className={`bcs-c none${!referenceOn && selected === 'none' ? ' on' : ''}`}
            aria-pressed={!referenceOn && selected === 'none'}
            disabled={state.locked}
            onClick={() => pick('none')}
          >
            <span className="pc">
              <span className="bcs-no">
                <StudioIcon kind="none" />
              </span>
              <i className="ck" aria-hidden="true">
                <StudioIcon kind="check" />
              </i>
            </span>
            <b>{uiText('Без стиля')}</b>
          </button>
        )}
        {railStyles.map(style => styleCard(style))}
        <button type="button" className="bcs-c more" onClick={() => setAll(true)}>
          <span className="pc">
            <span className="bcs-mi">
              <StudioIcon kind="photo" />
              <b>{policy.styles.length}</b>
            </span>
          </span>
          <b>{uiText('Все стили')}</b>
        </button>
      </div>
      {referenceField && <StudioReferencePlate reference={reference} use={referenceField.upload?.use ?? 'style'} />}
      {referenceOpen && referenceField && (
        <StudioSheet title="Свой референс" onClose={() => setReferenceOpen(false)}>
          <h2>{uiText('Свой референс')}</h2>
          <p>{uiText('Фото задаст стиль и атмосферу. Выбранный стиль заменится референсом.')}</p>
          <SwitchRow
            title={uiText('Право на картинку')}
            description={uiText('Подтверждаю право использовать загружаемые материалы')}
            icon={<StudioIcon kind="photo" />}
            checked={state.rights}
            disabled={state.locked}
            onChange={state.setRights}
          />
          <PhotoFieldInput field={referenceField} state={state} avatar={null} />
          <button type="button" className="btn" onClick={() => setReferenceOpen(false)}>
            {uiText('Готово')}
          </button>
        </StudioSheet>
      )}
      {all && (
        <StudioSheet title="Все стили" onClose={() => setAll(false)}>
          <h2>{uiText('Все стили')}</h2>
          {policy.styles.length > 12 && (
            <div className="search studio-style-search">
              <StudioIcon kind="search" />
              <input type="search" aria-label={uiText('Найти стиль')} value={query} onChange={event => setQuery(event.target.value)} />
            </div>
          )}
          <ChoiceTiles
            label={uiText('Стили')}
            choices={[
              { value: 'all', label: uiText('Все') },
              { value: 'pop', label: uiText('Популярные') },
              { value: 'new', label: uiText('Новые') },
            ]}
            value={tab}
            onChange={setTab}
          />
          {!!themes.length && (
            <div className="chips studio-style-themes" role="group" aria-label={uiText('Тема стиля')}>
              {themes.map(value => (
                <button
                  key={value}
                  type="button"
                  className={`chip${theme === value ? ' on' : ''}`}
                  aria-pressed={theme === value}
                  onClick={() => setTheme(current => (current === value ? '' : value))}
                >
                  {value}
                </button>
              ))}
            </div>
          )}
          <div className="studio-style-grid">
            {none && !query && !theme && tab === 'all' && (
              <div className="studio-style-item">
                <button
                  type="button"
                  className={`bcs-c none${!referenceOn && selected === 'none' ? ' on' : ''}`}
                  aria-pressed={!referenceOn && selected === 'none'}
                  disabled={state.locked}
                  onClick={() => pick('none')}
                >
                  <span className="pc">
                    <span className="bcs-no">
                      <StudioIcon kind="none" />
                    </span>
                    <i className="ck" aria-hidden="true">
                      <StudioIcon kind="check" />
                    </i>
                  </span>
                  <b>{uiText('Без стиля')}</b>
                </button>
              </div>
            )}
            {styles.map(style => styleCard(style, true))}
          </div>
          {!styles.length && (
            <div className="empty pf-empty" role="status">
              <b>{uiText('Ничего не нашлось')}</b>
              <p>{uiText('Попробуйте другое слово или сбросьте тему')}</p>
            </div>
          )}
          {!held && <p className="studio-hold-hint">{uiText('Нажмите, чтобы выбрать · Удерживайте, чтобы посмотреть примеры')}</p>}
        </StudioSheet>
      )}
      {card && (
        <StudioSheet title={card.name} onClose={closePreview}>
          <p className="studio-style-kicker">{uiText('Стиль STEOS')}</p>
          <h2>{card.name}</h2>
          {(card.hit || card.isNew || !!card.themes.length) && (
            <p className="studio-style-tags">
              {[...(card.hit ? [uiText('Хит')] : []), ...(card.isNew ? [uiText('Новое')] : []), ...card.themes].join(' · ')}
            </p>
          )}
          {card.description && <p>{card.description}</p>}
          {card.examples.some(url => studioMediaUrl(url)) && (
            <h3>
              {uiText('Примеры')} · <span className="mono">{card.examples.filter(url => studioMediaUrl(url)).length}</span>
            </h3>
          )}
          <div className="photo-result-grid">
            {card.examples
              .filter(url => studioMediaUrl(url))
              .map((url, index) => (
                <img key={index} src={studioMediaUrl(url)} alt={`${uiText('Пример')} ${index + 1}`} />
              ))}
          </div>
          {!card.examples.some(url => studioMediaUrl(url)) && <p>{uiText('Примеры этого стиля пока не опубликованы.')}</p>}
          <h3>{uiText('О стиле')}</h3>
          <dl className="studio-style-about">
            {!!card.themes.length && (
              <>
                <dt>{uiText('Подходит для')}</dt>
                <dd>{card.themes.join(', ')}</dd>
              </>
            )}
            {(unitPrice !== undefined || (state.form?.expected_price ?? 0) > 0) && (
              <>
                <dt>{uiText('Цена')}</dt>
                <dd>
                  <StudioUnitPrice kind={unit} price={unitPrice ?? state.form?.expected_price} total={unitTotal} />
                </dd>
              </>
            )}
          </dl>
          <button type="button" className="btn" disabled={state.locked} onClick={() => pick(card.id)}>
            {uiText(`Выбрать «${card.name}»`)}
          </button>
          <button type="button" className="btn line" onClick={closePreview}>
            {uiText('Назад')}
          </button>
        </StudioSheet>
      )}
    </section>
  );
}
